import React from 'react';
import {
    ExpandMore,
    CalendarMonth,
} from '@mui/icons-material';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Chip,
    Grid,
    Stack,
    Typography,
    Box,
} from '@mui/material';
import moment from 'moment';

const DayWiseDetailsSection = ({ dayWiseDetails, colors }) => {
    if (!dayWiseDetails || dayWiseDetails.length === 0) {
        return null;
    }

    const showAccordion = dayWiseDetails.length > 3;

    const DayWiseContent = () => (
        <Stack spacing={1}>
            {dayWiseDetails.map((day, idx) => {
                const dayTotal =
                    (day.amount || 0) +
                    (day.extra_bed_price || 0) +
                    (day.meal_plan_price || 0);

                return (
                    <Box
                        key={day.date || idx}
                        sx={{
                            p: 1.5,
                            borderRadius: 1,
                            bgcolor: colors.primary[400],
                            border: `1px solid ${colors.grey?.[700] || "#424242"}`,
                        }}
                    >
                        <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            mb={1}
                        >
                            <Chip
                                icon={<CalendarMonth sx={{ fontSize: 16 }} />}
                                label={moment.unix(day.date).format("DD MMM YYYY")}
                                size="small"
                                sx={{
                                    bgcolor: colors.blueAccent?.[700] || "#1976d2",
                                    color: "#fff",
                                    fontWeight: "bold",
                                }}
                            />
                            <Typography
                                fontWeight="bold"
                                sx={{ color: colors.greenAccent?.[400] || "#4caf50" }}
                            >
                                ₹{dayTotal.toLocaleString("en-IN")}
                            </Typography>
                        </Stack>

                        <Grid container spacing={1}>
                            <Grid item xs={4}>
                                <Typography
                                    variant="caption"
                                    color={colors.grey?.[400] || "text.secondary"}
                                >
                                    Room Rate
                                </Typography>
                                <Typography variant="body2" fontWeight="500">
                                    ₹{(day.amount || 0).toLocaleString("en-IN")}
                                </Typography>
                            </Grid>
                            <Grid item xs={4}>
                                <Typography
                                    variant="caption"
                                    color={colors.grey?.[400] || "text.secondary"}
                                >
                                    Extra Bed
                                </Typography>
                                <Typography variant="body2" fontWeight="500">
                                    ₹{(day.extra_bed_price || 0).toLocaleString("en-IN")}
                                </Typography>
                            </Grid>
                            <Grid item xs={4}>
                                <Typography
                                    variant="caption"
                                    color={colors.grey?.[400] || "text.secondary"}
                                >
                                    Meal Plan
                                </Typography>
                                <Typography variant="body2" fontWeight="500">
                                    ₹{(day.meal_plan_price || 0).toLocaleString("en-IN")}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Box>
                );
            })}
        </Stack>
    );

    // Calculate total from day-wise details
    const totalFromDayWise = dayWiseDetails.reduce((sum, day) => {
        return (
            sum +
            (day.amount || 0) +
            (day.extra_bed_price || 0) +
            (day.meal_plan_price || 0)
        );
    }, 0);

    if (showAccordion) {
        return (
            <Accordion
                sx={{
                    bgcolor: colors.primary[500],
                    border: `1px solid ${colors.grey?.[700] || "#424242"}`,
                    "&:before": { display: "none" },
                    boxShadow: "none",
                }}
            >
                <AccordionSummary
                    expandIcon={<ExpandMore sx={{ color: colors.grey?.[300] }} />}
                    sx={{
                        minHeight: 48,
                        "& .MuiAccordionSummary-content": { my: 1 },
                    }}
                >
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        width="100%"
                        pr={2}
                    >
                        <Typography fontWeight="500">
                            Day-wise Rates ({dayWiseDetails.length} days)
                        </Typography>
                        <Typography
                            fontWeight="bold"
                            sx={{ color: colors.greenAccent?.[400] || "#4caf50" }}
                        >
                            Total: ₹{totalFromDayWise.toLocaleString("en-IN")}
                        </Typography>
                    </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                    <DayWiseContent />
                </AccordionDetails>
            </Accordion>
        );
    }

    // Show directly if 3 or fewer days
    return (
        <Box>
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                mb={1}
            >
                <Typography fontWeight="500" variant="body2">
                    Day-wise Rates
                </Typography>
                <Typography
                    variant="body2"
                    fontWeight="bold"
                    sx={{ color: colors.greenAccent?.[400] || "#4caf50" }}
                >
                    Total: ₹{totalFromDayWise.toLocaleString("en-IN")}
                </Typography>
            </Stack>
            <DayWiseContent />
        </Box>
    );
};

export default DayWiseDetailsSection;